import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Outlet, Route, Routes, useNavigate } from 'react-router-dom';
import PaymentVoucher from './paymentVoucher';

import A5PaymentVoucher from './paymentVoucherA5';
import AuthApp from './LoginAndSignup/loginAndSignUp';
import Navbar from './components/navbar';
import EditPaymentVoucher from './editPaymentVoucher';
import EditPaymentVoucherForSuperAdmin from './editPaymentVoucherForSuperAdmin';
import UserManagement from './users/users';

import { FileDown } from 'lucide-react';
import { ErrorAlert } from './components/alertBox';
import * as XLSX from 'xlsx-js-style';
import SSOLogin from './sso-login/sso-login';


function PaymentVouchersList() {
  const navigate = useNavigate();
  const [paymentVouchers, setPaymentVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('voucher_no');
  const [sortOrder, setSortOrder] = useState('DESC');

  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isFiltered, setIsFiltered] = useState(false);

  const [isSearchActive, setIsSearchActive] = useState(false);
  const [currentSearchTerm, setCurrentSearchTerm] = useState('');
  const [isDateFilteredSearch, setIsDateFilteredSearch] = useState(false);
  const [transferMethod, setTransferMethod] = useState('');
  const [appliedTransferMethod, setAppliedTransferMethod] = useState('');
  const [userRole, setUserRole] = useState('')

  const fetchPaymentVouchers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        limit: pagination.limit.toString(),
        sortBy: sortBy,
        sortOrder: sortOrder
      });
      if (transferMethod) params.append('transferMethod', transferMethod);

      const response = await fetch(`https://payvoucher.netgateerp.lk/api/users/payment-vouchers?${params}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch payment vouchers.');
      }

      if (!data.success) {
        throw new Error(data.message || 'API returned unsuccessful response');
      }

      const vouchers = data.data?.vouchers || [];
      const paginationData = {
        page: 1, // Always start at page 1 for initial fetch
        limit: data.data?.pagination?.limit || 10,
        total: 0,
        totalPages: 0,
        hasNext: data.data?.pagination?.hasNext || false,
        hasPrev: false, // First page never has previous
        firstDocId: data.data?.pagination?.firstDocId || null,
        lastDocId: data.data?.pagination?.lastDocId || null
      };

      setPaymentVouchers(vouchers);
      console.log('Fetched payment vouchers:', vouchers);
      setPagination(paginationData);

    } catch (err) {
      console.error('Error fetching payment vouchers:', err);
      setError(err.message);
      setPaymentVouchers([]);
      setPagination({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrev: false,
        firstDocId: null,
        lastDocId: null
      });
    } finally {
      setLoading(false);
    }
  }, [sortBy, sortOrder, pagination.limit, appliedTransferMethod]); // Remove pagination dependencies

  useEffect(() => {
    if (isSearchActive && currentSearchTerm) {
      if (isFiltered && startDate && endDate) {
        handleDateFilteredSearch({ preventDefault: () => { } }, currentSearchTerm);
      } else {
        handleSearch({ preventDefault: () => { } }, 1, currentSearchTerm);
      }
    } else if (isFiltered && startDate && endDate) {
      handleDateFilter({ preventDefault: () => { } });
    } else {
      fetchPaymentVouchers();
    }

    const getUserRole = () => {
      const userData = localStorage.getItem('user')
      if (userData) {
        const user = JSON.parse(userData)
        setUserRole(user.role || 'Admin')
      }
    }

    getUserRole()
  }, [fetchPaymentVouchers, appliedTransferMethod, isSearchActive, isFiltered, currentSearchTerm]);


  const handleSearch = useCallback(async (e, page = 1, searchTerm = '', sort = sortBy, order = sortOrder) => {
    if (e) e.preventDefault();
    try {
      console.log("Searching payment vouchers with params:", searchTerm);
      setIsSearchActive(true);
      setCurrentSearchTerm(searchTerm);
      const response = await fetch(`https://payvoucher.netgateerp.lk/api/users/payment-vouchers/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ page: page.toString(), limit: pagination.limit.toString(), sortBy: sort, search: searchTerm, sortOrder: order, transferMethod: appliedTransferMethod })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch payment vouchers.');
      }
      if (!data.success) {
        throw new Error(data.message || 'API returned unsuccessful response');
      }
      const vouchers = data.data?.vouchers || [];
      const paginationData = data.data?.pagination || {
        page: 1, limit: 10, total: 0, totalPages: 0, hasNext: false, hasPrev: false
      };
      setPaymentVouchers(vouchers);
      setPagination(paginationData);
    } catch (err) {
      console.error('Error fetching payment vouchers:', err);
      setError(err.message);
      setPaymentVouchers([]);
      setPagination({
        page: 1, limit: 10, total: 0, totalPages: 0, hasNext: false, hasPrev: false
      });
    } finally {
      setLoading(false);
    }
  }, [sortBy, sortOrder, pagination.limit, appliedTransferMethod]);



  const handleSort = (column) => {
    const newOrder = sortBy === column && sortOrder === 'ASC' ? 'DESC' : 'ASC';
    setSortBy(column);
    setSortOrder(newOrder);
    fetchPaymentVouchers(pagination.page, search, column, newOrder);
  };

  const handlePageChange = async (newPage) => {
    try {
      setLoading(true);
      setError(null);

      let endpoint = '';

      // Check if we're in search mode
      if (isSearchActive && currentSearchTerm) {
        // Use search pagination endpoints
        if (newPage > pagination.page) {
          endpoint = `https://payvoucher.netgateerp.lk/api/users/payment-vouchers/search/next`;
        } else if (newPage < pagination.page) {
          endpoint = `https://payvoucher.netgateerp.lk/api/users/payment-vouchers/search/previous`;
        } else {
          return;
        }

        const body = {
          search: currentSearchTerm,
          sortBy: sortBy,
          sortOrder: sortOrder,
          transferMethod: appliedTransferMethod
        };

        if (newPage > pagination.page) {
          body.lastDocId = pagination.lastDocId;
        } else {
          body.firstDocId = pagination.firstDocId;
        }

        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(body)
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Failed to fetch payment vouchers.');
        }

        if (!data.success) {
          throw new Error(data.message || 'API returned unsuccessful response');
        }

        const vouchers = data.data?.vouchers || [];
        const paginationData = {
          page: newPage,
          limit: data.data?.pagination?.limit || 10,
          total: 0,
          totalPages: 0,
          hasNext: data.data?.pagination?.hasNext || false,
          hasPrev: data.data?.pagination?.hasPrev || false,
          firstDocId: data.data?.pagination?.firstDocId || null,
          lastDocId: data.data?.pagination?.lastDocId || null
        };

        setPaymentVouchers(vouchers);
        setPagination(paginationData);

      } else {
        // Regular pagination (your existing code)
        const params = new URLSearchParams({
          limit: pagination.limit.toString(),
          sortBy: sortBy,
          sortOrder: sortOrder
        });
        if (appliedTransferMethod) params.append('transferMethod', appliedTransferMethod);

        if (newPage > pagination.page) {
          params.append('lastDocId', pagination.lastDocId);
          endpoint = `https://payvoucher.netgateerp.lk/api/users/payment-vouchers/next?${params}`;
        } else if (newPage < pagination.page) {
          params.append('firstDocId', pagination.firstDocId);
          endpoint = `https://payvoucher.netgateerp.lk/api/users/payment-vouchers/previous?${params}`;
        } else {
          return;
        }

        const response = await fetch(endpoint);
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Failed to fetch payment vouchers.');
        }

        if (!data.success) {
          throw new Error(data.message || 'API returned unsuccessful response');
        }

        const vouchers = data.data?.vouchers || [];
        const paginationData = {
          page: newPage,
          limit: data.data?.pagination?.limit || 10,
          total: 0,
          totalPages: 0,
          hasNext: data.data?.pagination?.hasNext || false,
          hasPrev: data.data?.pagination?.hasPrev || false,
          firstDocId: data.data?.pagination?.firstDocId || null,
          lastDocId: data.data?.pagination?.lastDocId || null
        };

        setPaymentVouchers(vouchers);
        setPagination(paginationData);
      }

    } catch (err) {
      console.error('Error changing page:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVoucher = () => {
    navigate('/home/payment-voucher');
  };

  const handleRetry = () => {
    fetchPaymentVouchers(1, '', 'voucher_no', 'DESC');
  };

  const formatCurrency = (rs, cts) => {
    const total = parseFloat(rs || 0) + (parseFloat(cts || 0) / 100);
    return `Rs. ${total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit'
    });
  };

  const handleDateFilteredSearch = useCallback(async (e, searchTerm = '') => {
    if (e) e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      console.log("Searching within date-filtered vouchers:", searchTerm);
      if (!startDate || !endDate) {
        setError('Date filter is required for filtered search');
        setLoading(false);
        return;
      }
      setIsSearchActive(true);
      setCurrentSearchTerm(searchTerm);
      setIsDateFilteredSearch(true);
      const response = await fetch(`https://payvoucher.netgateerp.lk/api/users/payment-vouchers/searchWithDate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          page: '1',
          limit: pagination.limit.toString(),
          sortBy: sortBy,
          search: searchTerm,
          sortOrder: sortOrder,
          startDate: startDate,
          endDate: endDate,
          transferMethod: appliedTransferMethod
        })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to search payment vouchers.');
      }
      if (!data.success) {
        throw new Error(data.message || 'API returned unsuccessful response');
      }
      const vouchers = data.data?.vouchers || [];
      const paginationData = {
        page: 1,
        limit: data.data?.pagination?.limit || 10,
        total: data.data?.pagination?.total || 0,
        totalPages: data.data?.pagination?.totalPages || 0,
        hasNext: data.data?.pagination?.hasNext || false,
        hasPrev: false,
        firstDocId: data.data?.pagination?.firstDocId || null,
        lastDocId: data.data?.pagination?.lastDocId || null
      };
      setPaymentVouchers(vouchers);
      console.log('Fetched date-filtered search results:', vouchers);
      setPagination(paginationData);
    } catch (err) {
      console.error('Error searching date-filtered vouchers:', err);
      setError(err.message);
      setPaymentVouchers([]);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, sortBy, sortOrder, pagination.limit, appliedTransferMethod]);

  const fetchFilteredCount = async (start, end) => {
    try {
      const params = new URLSearchParams({
        startDate: start,
        endDate: end
      });

      const response = await fetch(
        `https://payvoucher.netgateerp.lk/api/users/payment-vouchers/countWithDate?${params}`
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          console.log('Total filtered records:', data.data.count);
          // You can update state to show this count in UI if needed
          // setTotalFilteredCount(data.data.count);
        }
      }
    } catch (err) {
      console.error('Error fetching filtered count:', err);
    }
  };

  const handleDateFilter = useCallback(async (e) => {
    if (e) e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      if (!startDate || !endDate) {
        // Apply the selected transfer method regardless of whether it's a specific one or "All Methods"
        setAppliedTransferMethod(transferMethod);
        setIsFiltered(false); // We are not in a date-filtered state
        setLoading(false);
        return;
      }
      setAppliedTransferMethod(transferMethod);
      const params = new URLSearchParams({
        limit: pagination.limit.toString(),
        sortBy: sortBy,
        sortOrder: sortOrder,
        startDate: startDate,
        endDate: endDate
      });
      if (transferMethod) params.append('transferMethod', transferMethod);
      const response = await fetch(
        `https://payvoucher.netgateerp.lk/api/users/payment-vouchers/currentWithDate?${params}`
      );
      if (!response.ok) {
        throw new Error('Failed to apply date filter.');
      }
      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || 'API returned unsuccessful response');
      }
      const vouchers = data.data?.vouchers || [];
      const paginationData = {
        page: 1,
        limit: data.data?.pagination?.limit || 10,
        total: 0,
        totalPages: 0,
        hasNext: data.data?.pagination?.hasNext || false,
        hasPrev: false,
        firstDocId: data.data?.pagination?.firstDocId || null,
        lastDocId: data.data?.pagination?.lastDocId || null
      };
      setPaymentVouchers(vouchers);
      setPagination(paginationData);
      setIsFiltered(true);
      fetchFilteredCount(startDate, endDate);
    } catch (err) {
      console.error('Error applying date filter:', err);
      setError(err.message);
      setPaymentVouchers([]);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, sortBy, sortOrder, pagination.limit, appliedTransferMethod, transferMethod]);


  const handleSearchRouter = async (e, page = 1, searchTerm = '', sort = sortBy, order = sortOrder) => {
    e.preventDefault();
    setAppliedTransferMethod(transferMethod);

    // If date filter is active, use date-filtered search
    if (isFiltered && startDate && endDate) {
      await handleDateFilteredSearch(e, searchTerm);
    } else {
      // Use the existing search function
      await handleSearch(e, page, searchTerm, sort, order);
    }
  };
  const handleDateFilteredSearchPageChange = async (newPage) => {
    try {
      setLoading(true);
      setError(null);

      console.log("Changing page for date-filtered search:", newPage);

      const body = {
        search: currentSearchTerm,
        limit: pagination.limit.toString(),
        sortBy: sortBy,
        sortOrder: sortOrder,
        startDate: startDate,
        endDate: endDate,
        page: newPage.toString(),
        transferMethod: appliedTransferMethod
      };

      const response = await fetch(`https://payvoucher.netgateerp.lk/api/users/payment-vouchers/searchWithDate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch payment vouchers.');
      }

      if (!data.success) {
        throw new Error(data.message || 'API returned unsuccessful response');
      }

      const vouchers = data.data?.vouchers || [];
      const paginationData = {
        page: newPage,
        limit: data.data?.pagination?.limit || 10,
        total: data.data?.pagination?.total || 0,
        totalPages: data.data?.pagination?.totalPages || 0,
        hasNext: data.data?.pagination?.hasNext || false,
        hasPrev: data.data?.pagination?.hasPrev || false,
        firstDocId: data.data?.pagination?.firstDocId || null,
        lastDocId: data.data?.pagination?.lastDocId || null
      };

      setPaymentVouchers(vouchers);
      setPagination(paginationData);

    } catch (err) {
      console.error('Error changing page for date-filtered search:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };



  const getSortIcon = (column) => {
    if (sortBy !== column) return (
      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
      </svg>
    );
    return sortOrder === 'ASC' ? (
      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  const handleViewVoucher = (voucher) => {

    console.log('Viewing voucher:', voucher);
    console.log(' voucher items in voucher data:', voucher.items);
    console.log(' voucher items in voucher data:', voucher.items.description);




    setSelectedVoucher(voucher);
    setIsModalOpen(true);
  };
  const handleEditVoucher = (voucher) => {
    console.log("User role:", userRole)
    if ('admin' === userRole.toString().toLowerCase()) {
      navigate('/home/payment-voucher-edit', { state: { voucherDoc: voucher.id } });
    } else {
      navigate('/home/payment-voucher-edit-forSuperAdmin', { state: { voucherDoc: voucher.id } });
    }

  }

  const handleFilteredPageChange = async (newPage) => {
    try {
      setLoading(true);
      setError(null);

      let endpoint = '';
      const params = new URLSearchParams({
        limit: pagination.limit.toString(),
        sortBy: sortBy,
        sortOrder: sortOrder,
        startDate: startDate,
        endDate: endDate
      });
      if (appliedTransferMethod) params.append('transferMethod', appliedTransferMethod);

      if (newPage > pagination.page) {
        // Going forward - use next endpoint
        if (!pagination.lastDocId) {
          throw new Error('No last document ID available for pagination');
        }
        params.append('lastDocId', pagination.lastDocId);
        console.log("Last doc id for pagination on filtered data:", pagination.lastDocId);
        endpoint = `https://payvoucher.netgateerp.lk/api/users/payment-vouchers/nextWithDate?${params}`;
      } else if (newPage < pagination.page) {
        // Going backward - use previous endpoint
        if (!pagination.firstDocId) {
          throw new Error('No first document ID available for pagination');
        }
        params.append('firstDocId', pagination.firstDocId);
        console.log("First doc id for pagination on filtered data:", pagination.firstDocId);
        endpoint = `https://payvoucher.netgateerp.lk/api/users/payment-vouchers/previousWithDate?${params}`;
      } else {
        return; // Same page, no action needed
      }

      const response = await fetch(endpoint);
      const data = await response.json();

      console.log("Pagination data on filtered page change:", data);

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch payment vouchers.');
      }

      if (!data.success) {
        throw new Error(data.message || 'API returned unsuccessful response');
      }

      const vouchers = data.data?.vouchers || [];
      const paginationData = {
        page: newPage,
        limit: data.data?.pagination?.limit || 10,
        total: 0,
        totalPages: 0,
        hasNext: data.data?.pagination?.hasNext || false,
        hasPrev: data.data?.pagination?.hasPrev || false,
        firstDocId: data.data?.pagination?.firstDocId || null,
        lastDocId: data.data?.pagination?.lastDocId || null
      };

      setPaymentVouchers(vouchers);
      setPagination(paginationData);

    } catch (err) {
      console.error('Error changing filtered page:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  const pageChange = (newPage) => {
    if (isSearchActive && isDateFilteredSearch) {
      // Search within date-filtered data
      console.log("Handling date-filtered search page change to page:", newPage);
      handleDateFilteredSearchPageChange(newPage);
    } else if (isFiltered) {
      // Date filter only (no search)
      console.log("Handling filtered page change to page:", newPage);
      handleFilteredPageChange(newPage);
    } else if (isSearchActive && currentSearchTerm) {
      // Regular search (no date filter)
      console.log("Handling search page change to page:", newPage);
      handlePageChange(newPage);
    } else {
      // Regular pagination (no filter, no search)
      handlePageChange(newPage);
    }
  };





  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="flex justify-center items-center min-h-screen">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
            <div className="text-lg font-medium text-gray-700">Loading payment vouchers...</div>
            <div className="text-sm text-gray-500 mt-1">Please wait while we fetch your data</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Payment Vouchers</h1>
            <p className="text-gray-600">Manage and track your payment vouchers</p>
          </div>
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-12">
            <div className="text-center max-w-md mx-auto">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-6">
                <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Failed to Load Payment Vouchers</h2>
              <p className="text-gray-600 mb-2">We encountered an issue while loading your data</p>
              <p className="text-sm text-red-600 mb-8 font-medium">{error}</p>
              <button
                onClick={handleRetry}
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium rounded-xl hover:from-blue-700 hover:to-blue-800 focus:ring-4 focus:ring-blue-200 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }


  const handleClearDateFilter = () => {
    setStartDate('');
    setEndDate('');
    setTransferMethod('');
    setAppliedTransferMethod('');
    setIsFiltered(false);
    setIsDateFilteredSearch(false);
    setIsSearchActive(false);
    setCurrentSearchTerm('');
    setSearch('');
    setError(null);

    // Reset pagination and fetch all vouchers
    setPagination({
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 0,
      hasNext: false,
      hasPrev: false,
      firstDocId: null,
      lastDocId: null
    });

    // Fetch all vouchers again
    fetchPaymentVouchers();
  };
  const handleClearSearch = () => {
    setSearch('');
    setIsSearchActive(false);
    setCurrentSearchTerm('');
    setIsDateFilteredSearch(false);

    // If date filter is still active, go back to date-filtered view
    if (isFiltered && startDate && endDate) {
      handleDateFilter();
    } else {
      fetchPaymentVouchers();
    }
  };

  const timestampToFormattedDate = (timestamp) => {
    const date = new Date(timestamp);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  }
  const FormatTransferMethod = (method) => {
    const labels = {
      'peoples_bank': "People's Bank",
      'seylan_bank': "Seylan Bank",
      'cash': "Cash"
    };
    return labels[method] || method || 'N/A';
  };

  const exportToExcel = async () => {
    try {
      setLoading(true);

      let dataToExport = [];

      // If filtered, fetch all filtered data
      if (isFiltered && startDate && endDate) {
        const params = new URLSearchParams({
          startDate: startDate,
          endDate: endDate,
          sortBy: sortBy,
          sortOrder: sortOrder,
          // Don't include limit and pagination params to get all data
        });

        const response = await fetch(
          `https://payvoucher.netgateerp.lk/api/users/payment-vouchers/export?${params}`
        );

        if (!response.ok) {
          throw new Error('Failed to fetch filtered data for export');
        }

        const data = await response.json();

        if (!data.success) {
          throw new Error(data.message || 'Failed to fetch data');
        }

        dataToExport = data.data?.vouchers || [];
      } else {
        // If not filtered, use current displayed data
        dataToExport = paymentVouchers;
      }

      if (dataToExport.length === 0) {
        ErrorAlert("No data to export");
        return;
      }


      // Prepare data for Excel
      const excelData = dataToExport.map(voucher => ({
        'Voucher No': voucher.voucher_no,
        'Date': timestampToFormattedDate(voucher.date),
        'Vendor': voucher.payable_to,
        'Description': voucher.description,
        'Transfer Method': FormatTransferMethod(voucher.transfer_method),
        'Payment Method': voucher.cheque_details,
        'Amount': parseFloat(voucher.total_rs) || 0, // Convert to number
        'GRN No': voucher.grn_no,
        'PO No': voucher.po_no
      }));

      // Create worksheet from data
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      // Set column widths for better readability
      worksheet['!cols'] = [
        { wch: 15 }, // Voucher No
        { wch: 12 }, // Date
        { wch: 30 }, // Vendor
        { wch: 30 }, // Description
        { wch: 15 }, // transfer details
        { wch: 15 }, // payment method details
        { wch: 15 }, // Amount
        { wch: 15 }, // GRN No
        { wch: 15 }  // PO No
      ];

      // Define table range (A1 to F + last row)
      const lastRow = excelData.length + 1; // +1 for header
      const tableRef = `A1:I${lastRow}`;

      // Add Excel Table
      worksheet['!autofilter'] = { ref: tableRef };

      // Get table range
      const range = XLSX.utils.decode_range(tableRef);

      // Define border style
      const borderStyle = {
        top: { style: "thin", color: { auto: 1 } },
        bottom: { style: "thin", color: { auto: 1 } },
        left: { style: "thin", color: { auto: 1 } },
        right: { style: "thin", color: { auto: 1 } }
      };

      // Apply styling to all populated cells
      for (let row = range.s.r; row <= range.e.r; row++) {
        for (let col = range.s.c; col <= range.e.c; col++) {
          const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });

          // Only style cells that exist (are populated)
          if (worksheet[cellAddress]) {
            // Initialize cell style if it doesn't exist
            if (!worksheet[cellAddress].s) {
              worksheet[cellAddress].s = {};
            }

            // Style header row (row 0)
            if (row === 0) {
              worksheet[cellAddress].s = {
                font: {
                  name: "Calibri",
                  sz: 11,
                  bold: true,
                  color: { rgb: "FFFFFF" }
                },
                fill: {
                  patternType: "solid",
                  fgColor: { rgb: "4472C4" },
                  bgColor: { rgb: "4472C4" }
                },
                alignment: {
                  horizontal: "center",
                  vertical: "center"
                },
                border: borderStyle
              };
            } else {
              // Add borders to data cells
              worksheet[cellAddress].s = {
                border: borderStyle,
                alignment: {
                  vertical: "center"
                }
              };

              // Apply number format with commas to Amount column (column G, index 6)
              if (col === 6) {
                worksheet[cellAddress].s.numFmt = '#,##0.00';
                worksheet[cellAddress].s.alignment = {
                  ...worksheet[cellAddress].s.alignment,
                  horizontal: "right"
                };
              }

              // Left align Description (column D, index 3)
              if (col === 3) {
                worksheet[cellAddress].s.alignment = {
                  ...worksheet[cellAddress].s.alignment,
                  horizontal: "left"
                };
              }
            }
          }
        }
      }

      // Create workbook and add worksheet
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Payment Vouchers');

      // Generate file name
      const fileName = isFiltered
        ? `payment_vouchers_${startDate}_to_${endDate}.xlsx`
        : `payment_vouchers_all.xlsx`;

      // Write buffer with full formatting support
      const wbout = XLSX.write(workbook, {
        bookType: 'xlsx',
        type: 'array',
        cellStyles: true
      });

      // Create blob and download
      const blob = new Blob([wbout], { type: 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);

    } catch (e) {
      console.log("Error in exporting to excel", e);
      ErrorAlert("Error in exporting to excel");
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <Navbar />
        <div className='p-6'>
          <div className="max-w-7xl mx-auto">
            {/* Header Section */}
            <div className="mb-8">
              <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
                <div>
                  <h1 className="text-4xl font-bold text-gray-900 mb-2">Payment Vouchers</h1>
                  <p className="text-gray-600">Manage and track your payment vouchers efficiently</p>
                </div>
                <button
                  onClick={handleCreateVoucher}
                  className="inline-flex items-center px-6 py-3 bg-[#1B2F69]  text-white font-medium rounded-xl hover:blue-100 focus:ring-2 focus:ring-blue-200 transition-all duration-200 shadow-lg hover:shadow-xl self-start lg:self-auto"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Create Payment Voucher
                </button>
              </div>
            </div>

            {/* Filters Section */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-6">
              <div className="flex items-center mb-3">
                <svg className="w-5 h-5 text-gray-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                <h3 className="text-lg font-semibold text-gray-900">Filter Vouchers</h3>
              </div>
              <form onSubmit={handleDateFilter} className="flex flex-col lg:flex-row gap-4 items-end">
                <div className="flex-1 w-full lg:w-auto">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Transfer Method
                  </label>
                  <select
                    value={transferMethod}
                    onChange={(e) => {
                      setTransferMethod(e.target.value);
                    }}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-200 focus:border-blue-400 transition-all duration-200"
                  >
                    <option value="">All Methods</option>
                    <option value="peoples_bank">People's Bank</option>
                    <option value="seylan_bank">Seylan Bank</option>
                    <option value="cash">Cash</option>
                  </select>
                </div>
                <div className="flex-1 w-full lg:w-auto">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-200 focus:border-blue-400 transition-all duration-200"
                  />
                </div>
                <div className="flex-1 w-full lg:w-auto">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-200 focus:border-blue-400 transition-all duration-200"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="submit"
                    className="px-6 py-3 bg-[#1B2F69] text-white font-medium rounded-xl hover:bg-blue-800 focus:ring-4 focus:ring-blue-200 transition-all duration-200 shadow-lg hover:shadow-xl whitespace-nowrap"
                  >
                    Apply Filter
                  </button>
                  {(startDate || endDate || transferMethod) && (
                    <button
                      type="button"
                      onClick={handleClearDateFilter}
                      className="px-6 py-3 bg-gray-600 text-white font-medium rounded-xl hover:bg-gray-700 focus:ring-4 focus:ring-gray-200 transition-all duration-200 whitespace-nowrap"
                    >
                      Clear All
                    </button>
                  )}
                </div>
              </form>
              {(startDate || endDate) && (
                <div className="mt-4 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <span className="font-semibold">Active Date Range: </span>
                    {startDate && endDate
                      ? `${formatDate(startDate)} - ${formatDate(endDate)}`
                      : startDate
                        ? `From ${formatDate(startDate)}`
                        : `Until ${formatDate(endDate)}`
                    }
                  </p>
                </div>
              )}
            </div>
            {/* Export Button - Only shown when date filter is active */}
            {isFiltered && (startDate || endDate) && (
              <div className="mb-6">
                <button
                  onClick={exportToExcel}
                  className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl font-medium transition-colors duration-200 shadow-lg shadow-green-200 flex items-center gap-2"
                >
                  <FileDown className="w-5 h-5" />
                  Export to Excel
                </button>
              </div>
            )}

            {/* Search Section */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-8">
              <form onSubmit={(e) => handleSearchRouter(e, 1, search)} className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder={isFiltered
                      ? "Search within filtered date range..."
                      : "Search by payable to or voucher no"}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-200 focus:border-blue-400 transition-all duration-200"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="submit"
                    className="px-8 py-3 bg-[#1B2F69] text-white font-medium rounded-xl hover:bg-blue-800 focus:ring-4 focus:ring-blue-200 transition-all duration-200 shadow-lg hover:shadow-xl whitespace-nowrap"
                  >
                    Search
                  </button>
                  {(isSearchActive || search) && (
                    <button
                      type="button"
                      onClick={handleClearSearch}
                      className="px-6 py-3 bg-gray-600 text-white font-medium rounded-xl hover:bg-gray-700 focus:ring-4 focus:ring-gray-200 transition-all duration-200 whitespace-nowrap"
                    >
                      Clear Search
                    </button>
                  )}
                </div>
              </form>

              {/* Active filters indicator */}
              {(isFiltered || isSearchActive) && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {isFiltered && (
                    <span className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Date: {formatDate(startDate)} - {formatDate(endDate)}
                    </span>
                  )}
                  {isSearchActive && currentSearchTerm && (
                    <span className="inline-flex items-center px-3 py-1 bg-purple-100 text-purple-800 text-sm font-medium rounded-full">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      Search: "{currentSearchTerm}"
                    </span>
                  )}
                </div>
              )}
            </div>

            {paymentVouchers.length === 0 && !loading ? (
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-12">
                <div className="text-center max-w-md mx-auto">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-6">
                    <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">No Payment Vouchers Found</h3>
                  <p className="text-gray-600 mb-8">
                    {isDateFilteredSearch
                      ? `No vouchers found matching "${currentSearchTerm}" in the date range ${formatDate(startDate)} - ${formatDate(endDate)}.`
                      : isFiltered
                        ? `No vouchers found in the date range ${formatDate(startDate)} - ${formatDate(endDate)}.`
                        : search
                          ? `No vouchers found matching "${search}". Try adjusting your search terms.`
                          : 'No payment vouchers have been created yet. Create your first voucher to get started.'
                    }
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    {(isDateFilteredSearch || search) && (
                      <button
                        onClick={handleClearSearch}
                        className="px-6 py-3 bg-gray-600 text-white font-medium rounded-xl hover:bg-gray-700 focus:ring-4 focus:ring-gray-200 transition-all duration-200"
                      >
                        Clear Search
                      </button>
                    )}
                    {isFiltered && (
                      <button
                        onClick={handleClearDateFilter}
                        className="px-6 py-3 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 focus:ring-4 focus:ring-blue-200 transition-all duration-200"
                      >
                        Clear Date Filter
                      </button>
                    )}
                    <button
                      onClick={handleCreateVoucher}
                      className="px-6 py-3 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white font-medium rounded-xl hover:from-emerald-700 hover:to-emerald-800 focus:ring-4 focus:ring-emerald-200 transition-all duration-200 shadow-lg hover:shadow-xl"
                    >
                      Create Voucher
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                        <tr>
                          <th
                            className="group px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200 transition-colors duration-200"
                            onClick={() => handleSort('voucherNo')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Voucher No</span>
                              {getSortIcon('voucherNo')}
                            </div>
                          </th>
                          <th
                            className="group px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200 transition-colors duration-200"
                            onClick={() => handleSort('date')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Date</span>
                              {getSortIcon('date')}
                            </div>
                          </th>
                          <th
                            className="group px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200 transition-colors duration-200"
                            onClick={() => handleSort('payableTo')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Payable To</span>
                              {getSortIcon('payableTo')}
                            </div>
                          </th>
                          <th
                            className="group px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200 transition-colors duration-200"
                            onClick={() => handleSort('totalRs')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Total Amount</span>
                              {getSortIcon('totalRs')}
                            </div>
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                            PO No
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                            GRN No
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                            Prepared By
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-100">
                        {paymentVouchers.map((voucher, index) => (
                          <tr key={voucher.id} className="hover:bg-blue-50/50 transition-colors duration-200">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-semibold text-gray-900">{voucher.voucherNo}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-700">{formatDate(voucher.date)}</div>
                            </td>
                            <td className="px-6 py-4 max-w-xs">
                              <div className="text-sm text-gray-900 truncate" title={voucher.payableTo}>
                                {voucher.payableTo}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-bold text-emerald-700 bg-emerald-50 px-3 py-1 rounded-full inline-block">
                                {formatCurrency(voucher.totalRs, voucher.totalCts)}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-700">{voucher.poNo || 'N/A'}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-700">{voucher.grnNo || 'N/A'}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-700">{voucher.preparedBy || 'N/A'}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex space-x-2">
                                <button
                                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-100 rounded-lg hover:bg-blue-200 focus:ring-2 focus:ring-blue-300 transition-all duration-200"
                                  onClick={() => handleViewVoucher(voucher)}
                                >
                                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                  </svg>
                                  View
                                </button>
                                <button
                                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-100 rounded-lg hover:bg-blue-200 focus:ring-2 focus:ring-blue-300 transition-all duration-200"
                                  onClick={() => handleEditVoucher(voucher)}
                                >
                                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                  Edit
                                </button>
                                {/* <button 
              className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-100 rounded-lg hover:bg-emerald-200 focus:ring-2 focus:ring-emerald-300 transition-all duration-200"
              onClick={() => console.log('Edit voucher', voucher.id)}
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit
            </button>  */}
                                {/* <button 
              className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-red-700 bg-red-100 rounded-lg hover:bg-red-200 focus:ring-2 focus:ring-red-300 transition-all duration-200"
              onClick={() => console.log('Delete voucher', voucher.id)}
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete
            </button>  */}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Pagination */}

                {paymentVouchers.length > 0 && (
                  <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mt-8">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                      <div className="text-sm text-gray-700 font-medium">
                        Page {pagination.page}
                      </div>

                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => pageChange(pagination.page - 1)}
                          disabled={!pagination.hasPrev}
                          className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${pagination.hasPrev
                            ? 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 focus:ring-2 focus:ring-blue-300'
                            : 'bg-gray-100 border border-gray-200 text-gray-400 cursor-not-allowed'
                            }`}
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                          Previous
                        </button>

                        {/* Page Numbers */}
                        <div className="flex items-center space-x-1">
                          {/* First page */}
                          {pagination.page > 3 && (
                            <>
                              <button
                                onClick={() => pageChange(1)}
                                className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200"
                              >
                                1
                              </button>
                              {pagination.page > 4 && (
                                <span className="px-2 text-gray-500">...</span>
                              )}
                            </>
                          )}

                          {/* Previous 2 pages */}
                          {pagination.page > 2 && (
                            <button
                              onClick={() => pageChange(pagination.page - 2)}
                              className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200"
                            >
                              {pagination.page - 2}
                            </button>
                          )}
                          {pagination.page > 1 && (
                            <button
                              onClick={() => pageChange(pagination.page - 1)}
                              className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200"
                            >
                              {pagination.page - 1}
                            </button>
                          )}

                          {/* Current page */}
                          <span className="px-3 py-2 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded-lg">
                            {pagination.page}
                          </span>

                          {/* Next 2 pages */}
                          {pagination.hasNext && (
                            <>
                              <button
                                onClick={() => pageChange(pagination.page + 1)}
                                className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200"
                              >
                                {pagination.page + 1}
                              </button>
                              <button
                                onClick={() => pageChange(pagination.page + 2)}
                                className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200"
                              >
                                {pagination.page + 2}
                              </button>
                              <span className="px-2 text-gray-500">...</span>
                            </>
                          )}
                        </div>

                        <button
                          onClick={() => pageChange(pagination.page + 1)}
                          disabled={!pagination.hasNext}
                          className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${pagination.hasNext
                            ? 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 focus:ring-2 focus:ring-blue-300'
                            : 'bg-gray-100 border border-gray-200 text-gray-400 cursor-not-allowed'
                            }`}
                        >
                          Next
                          <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {isModalOpen && selectedVoucher && (
        <A5PaymentVoucher
          voucherData={selectedVoucher}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedVoucher(null);
          }}
        />
      )}
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<AuthApp />} />
        <Route path="/sso-handler" element={<SSOLogin />} />

        {/* Group routes that share common elements */}
        <Route element={<>
          {/* Shared header/nav can go here */}
          <Outlet /> {/* Child routes render here */}
        </>}>
          <Route path="/home" element={<PaymentVouchersList />} />
          <Route path="/home/payment-voucher" element={<PaymentVoucher />} />
          <Route path="/home/payment-voucher-edit" element={<EditPaymentVoucher />} />
          <Route path="/home/payment-voucher-edit-forSuperAdmin" element={<EditPaymentVoucherForSuperAdmin />} />
          <Route path="/home/users" element={<UserManagement />} />

        </Route>
      </Routes>
    </BrowserRouter>
  );
}